import React, { FunctionComponent } from 'react'
import { Trans, t } from '@lingui/macro'

import { i18n } from 'utils/i18n'
import ArbitraryCodeEmbed from 'components/arbitraryCodeEmbed'
import { classNames } from 'utils/dom'

import {
  container,
  exerciseContent,
  exerciseTitle,
  image,
  exerciseOrder,
  exerciseInfo,
  exerciseInfoCell,
  exerciseNameCell,
  bold,
  time,
  exerciseRest,
  step,
} from './index.module.scss'

interface ExerciseProps {
  name: string
  repetitions: number
  secondsOfWork: number
  secondsOfRest: number
  image: {
    type: string
    fields: Contentful.ArbitraryEmbeddedCode['fields']
  }
}

interface WorkoutStepProps {
  text: string
  type: 'calories' | 'rest'
}

interface NodeProps {
  __typename: 'ContentfulExercise' | 'ContentfulWorkoutStep'
}

type WorkoutExercise =
  | (WorkoutStepProps & NodeProps)
  | (ExerciseProps & NodeProps)

export interface WorkoutProps {
  exercises: WorkoutExercise[]
  calories: number
  className?: string
}

const isWorkoutStep = (
  step: WorkoutExercise
): step is WorkoutStepProps & NodeProps =>
  step.__typename === 'ContentfulWorkoutStep'

const isExercise = (step: WorkoutExercise): step is ExerciseProps & NodeProps =>
  step.__typename === 'ContentfulExercise'

const parseSeconds = (totalSeconds: number) => ({
  minutes: Math.floor(totalSeconds / 60),
  seconds: totalSeconds % 60,
})

const formatTime = ({
  minutes,
  seconds,
}: {
  minutes: number
  seconds: number
}) => {
  const format = (part: number) => String(part).padStart(2, '0')
  return `${format(minutes)}:${format(seconds)}`
}

const TimeElement: FunctionComponent<{ seconds: number }> = ({
  seconds: totalSeconds,
}) => {
  const { minutes, seconds } = parseSeconds(totalSeconds)
  return (
    <time dateTime={`P${minutes}M,${seconds}S`} className={time}>
      {formatTime({ minutes, seconds })}
    </time>
  )
}

const Exercise: FunctionComponent<ExerciseProps & { index: number }> = ({
  index,
  name,
  repetitions,
  secondsOfWork,
  secondsOfRest,
  image: {
    type,
    fields: {
      iframeContent: { title, relativePath, includedUrls, initialAspectRatio },
    },
  },
}) => (
  <div>
    <div className={exerciseTitle}>
      <span className={classNames([bold, exerciseOrder])}>
        <Trans>Exercise {index}</Trans>
      </span>
      <Trans>(repeat {repetitions} times)</Trans>
    </div>
    <div className={exerciseContent}>
      <div data-type={type} className={image}>
        <ArbitraryCodeEmbed
          title={title}
          src={relativePath}
          initialAspectRatio={initialAspectRatio}
          includedUrls={includedUrls}
        />
      </div>
      <div className={exerciseInfo}>
        <div className={classNames([exerciseInfoCell, exerciseNameCell])}>
          <TimeElement seconds={secondsOfWork} />
          <div className={bold}>{name}</div>
        </div>
        <div className={classNames([exerciseInfoCell, exerciseRest])}>
          <TimeElement seconds={secondsOfRest} />
          <div className={bold}>
            <Trans>Rest</Trans>
          </div>
        </div>
      </div>
    </div>
  </div>
)

const WorkoutStep: FunctionComponent<WorkoutStepProps> = ({ text, type }) => (
  <div data-type={type} className={step}>
    {text}
  </div>
)

const Workout: FunctionComponent<WorkoutProps> = ({
  calories,
  exercises,
  className,
}) => {
  let exerciseIndex = 0
  return (
    <div className={classNames([container, className])}>
      {exercises.map((exercise) => {
        if (isWorkoutStep(exercise)) {
          return (
            <WorkoutStep
              type="rest"
              text={exercise.text}
              key={`workout-step-${exerciseIndex}`}
            />
          )
        } else if (isExercise(exercise)) {
          exerciseIndex++

          return (
            <Exercise
              {...exercise}
              index={exerciseIndex}
              key={`exercise-${exerciseIndex}`}
            />
          )
        }

        return null
      })}

      <WorkoutStep
        type="calories"
        text={i18n._(
          t('workout-success')`Success! You burned ${calories} calories.`
        )}
      />
    </div>
  )
}

export default Workout
